import GIF from "gif.js.optimized";
import { message } from "antd";
import {
  ImageCropData, MomentHeaderData, releaseCanvas, renderSponsorOverlay, renderMomentHeader, loadImage,
  buildImageFilterCss, minMomentCanvasExportWidth,
  momentHeaderDownloadScoreBoost,
  preloadMomentHeaderFonts,
  SCOREBOARD_RASTER_VERTICAL_BOOST,
} from "./sharedFunctions";
import { SCOREBOARD_LOGO_DOWNLOAD_MULT } from "./scoreboardConstants";

/** Frame delay for My Moments, fan download, and operator Downloads playback. 500ms = 2 FPS. */
export const GIF_FRAME_DELAY_MS = 500;

const blobToDataURL = async (blob) => {
  return new Promise<string>((resolve) => {
    const a = new FileReader();
    a.onload = (e) => {
      resolve(e.target.result as string);
    };
    a.readAsDataURL(blob);
  });
};

export type GifEncodeOptions = {
  workers?: number;
  /** gif.js: higher values trade quality for faster encoding */
  quality?: number;
  frameDelayMs?: number;
};

// MMNT-163 @ram Try this and let me know what you think...
export const renderAnimatedGifsFromCanvas = async (
  width: number,
  height: number,
  canvasWithImages: HTMLCanvasElement[],
  encode?: GifEncodeOptions,
): Promise<string> => {
  const workerUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${process.env.PUBLIC_URL || ""}/gif.worker.js`;
  const workers = encode?.workers ?? 2;
  const quality = encode?.quality ?? 10;
  const frameDelayMs = encode?.frameDelayMs ?? GIF_FRAME_DELAY_MS;
  return new Promise((resolve, reject) => {
    const gif = new GIF({
      workers,
      quality,
      width,
      height,
      copy: true,
      workerScript: workerUrl,
    });

    for (const canvasWithImage of canvasWithImages) {
      gif.addFrame(canvasWithImage, { delay: frameDelayMs });
    }

    gif.on("finished", (blob) => {
      const url = blobToDataURL(blob);
      resolve(url);
    });

    gif.on("error", () => {
      message.error("Something went wrong while creating a GIF for download");
      reject(null);
    });

    gif.render();
  });
};

// use gif JS package to create GIF
export const downloadGifImageGifJS = async (
  width: number,
  height: number,
  canvasWithImages: HTMLCanvasElement[],
): Promise<string> => {
  const workerUrl = `${typeof window !== "undefined" ? window.location.origin : ""}${process.env.PUBLIC_URL || ""}/gif.worker.js`;
  return new Promise((resolve, reject) => {
    const gif = new GIF({
      workers: 2,
      quality: 10,
      width,
      height,
      copy: true,
      workerScript: workerUrl,
    });

    for (let ind = 0; ind < canvasWithImages.length; ind += 1) {
      gif.addFrame(canvasWithImages[ind], { delay: GIF_FRAME_DELAY_MS });
    }
    gif.on("finished", (blob) => {
      const url = URL.createObjectURL(blob);
      resolve(url);
    });

    gif.on("error", () => {
      message.error("Something went wrong while creating a GIF for download");
      reject(null);
    });

    gif.render();
  });
};

// preparations for GIF creation
const convertLoadedImageToImageCanvas = (
  cropData: ImageCropData,
  canvasWidth: number, canvasHeight: number,
  img: HTMLImageElement, sponsorImage?: HTMLImageElement,
  headerData?: MomentHeaderData,
  forSharing?: boolean,
): HTMLCanvasElement => {
  const { x, y, width, height } = cropData;

  // Full-bleed render width for sponsor and/or header — must match renderMomentHeader(canvasWidth) argument
  const minExportW = minMomentCanvasExportWidth(!!headerData);
  let renderWidth = ((sponsorImage || headerData) && canvasWidth < minExportW)
    ? minExportW
    : canvasWidth;
  let renderHeight = canvasHeight * (renderWidth / canvasWidth);

  const MAX_SHARE_GIF_EDGE = 720;
  if (forSharing && Math.max(renderWidth, renderHeight) > MAX_SHARE_GIF_EDGE) {
    const scale = MAX_SHARE_GIF_EDGE / Math.max(renderWidth, renderHeight);
    renderWidth = Math.round(renderWidth * scale);
    renderHeight = Math.round(renderHeight * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = renderWidth;
  canvas.height = renderHeight;
  const context = canvas.getContext("2d");

  if (!context) {
    console.log("No memory for canvas");
    return canvas;
  }

  // scale all images to the same area with first image sizes
  const ratioX = width / canvasWidth;
  const ratioY = height / canvasHeight;
  const ratio = ratioX > ratioY ? ratioX : ratioY;
  const drawWidth = width / ratio;
  const drawHeight = height / ratio;
  const offsetX = (canvasWidth - drawWidth) / 2;
  const offsetY = (canvasHeight - drawHeight) / 2;

  // Scale draw region to render dimensions
  const scale = renderWidth / canvasWidth;
  const scaledDrawWidth = drawWidth * scale;
  const scaledDrawHeight = drawHeight * scale;
  const scaledOffsetX = (renderWidth - scaledDrawWidth) / 2;
  const scaledOffsetY = (renderHeight - scaledDrawHeight) / 2;

  // Apply image filter and custom adjustments
  const filter = buildImageFilterCss(cropData.filter, cropData.customAdjustments);
  context.filter = filter;

  context.drawImage(img, x, y, width, height,
    scaledOffsetX, scaledOffsetY, scaledDrawWidth, scaledDrawHeight);

  context.filter = "none";

  if (sponsorImage) {
    renderSponsorOverlay(
      context,
      sponsorImage,
      0,
      0,
      renderWidth,
      renderHeight,
    );
  }

  if (headerData) {
    renderMomentHeader(
      context,
      renderWidth,
      headerData,
      forSharing,
      false,
      forSharing ? 1 : SCOREBOARD_RASTER_VERTICAL_BOOST,
      forSharing ? 1 : SCOREBOARD_LOGO_DOWNLOAD_MULT,
      forSharing ? 1 : momentHeaderDownloadScoreBoost(true),
    );
  }

  return canvas;
};

// prepare GIF for playing
export const prepareGifForPlayForLoadedImages = async (
  images: HTMLImageElement[],
  positions: ImageCropData[],
  sponsorImage?: HTMLImageElement,
  headerData?: MomentHeaderData,
): Promise<string | null> => {
  console.log("prepareGifForPlayForLoadedImages()");
  try {
    const { width, height } = positions[0];
    const canvasWithImages = images.map((image) => {
      return convertLoadedImageToImageCanvas(
        positions[0], width, height, image, sponsorImage,
        headerData,
      );
    });

    if (!canvasWithImages.length) {
      return null;
    }

    const gifW = canvasWithImages[0].width;
    const gifH = canvasWithImages[0].height;

    const gifImage = await downloadGifImageGifJS(
      gifW, gifH, canvasWithImages,
    );

    for (let ind = 0, len = canvasWithImages.length; ind < len; ind += 1) {
      releaseCanvas(canvasWithImages[ind]);
    }

    return gifImage;
  } catch (err) {
    console.error(err);
    message.error("Something went wrong while creating a GIF");

    return null;
  }
};

// prepare GIF for playing
export const buildAnimatedGifFromImageElements = async (
  images: HTMLImageElement[],
  sizes: ImageCropData,
  positions: ImageCropData[],
  sponsorImage?: HTMLImageElement,
  headerData?: MomentHeaderData,
  forSharing?: boolean,
): Promise<string | null> => {
  console.log("buildAnimatedGifFromImageElements():", images.map((i) => i?.src));
  try {
    const { width, height } = sizes;

    if (headerData && !forSharing) {
      const minExportW = minMomentCanvasExportWidth(true);
      const renderWidth = width < minExportW ? minExportW : width;
      try {
        await preloadMomentHeaderFonts(renderWidth, SCOREBOARD_RASTER_VERTICAL_BOOST);
      } catch {
        /* best-effort — render with fallback fonts */
      }
    }

    // delete undefined images
    const existingImages = images.filter((image) => image);
    const canvasWithImages = existingImages.map((image, index) => {
      const position = index < positions.length ? positions[index] : sizes;
      return convertLoadedImageToImageCanvas(
        position, width, height, image, sponsorImage,
        headerData,
        forSharing,
      );
    });

    if (!canvasWithImages.length) {
      return null;
    }

    const gifW = canvasWithImages[0].width;
    const gifH = canvasWithImages[0].height;

    const gifImage = await renderAnimatedGifsFromCanvas(
      gifW,
      gifH,
      canvasWithImages,
      forSharing
        ? { workers: 2, quality: 22, frameDelayMs: GIF_FRAME_DELAY_MS }
        : { workers: 2, quality: 10, frameDelayMs: GIF_FRAME_DELAY_MS },
    );

    for (let ind = 0, len = canvasWithImages.length; ind < len; ind += 1) {
      releaseCanvas(canvasWithImages[ind]);
    }

    return gifImage;
  } catch (err) {
    console.error(err);
    message.error("Something went wrong while creating a GIF");

    return null;
  }
};

// prepare GIF
export const buildAnimatedGifFromLinks = async (
  links: string[],
  sizes: ImageCropData,
  positions: ImageCropData[],
  sponsorLink?: string,
  headerData?: MomentHeaderData,
  forSharing?: boolean,
): Promise<string | null> => {
  const images: HTMLImageElement[] = [];

  for (let ind = 0; ind < links.length; ind += 1) {
    // eslint-disable-next-line no-await-in-loop
    const imageResult = await loadImage(links[ind]);
    if (imageResult?.image) {
      images.push(imageResult.image);
    }
  }

  let sponsorImage = null;
  try {
    const sponsorImageResult = await loadImage(sponsorLink);
    sponsorImage = sponsorImageResult?.image || null;
  } catch (err) {
    console.log("cannot load sponsor overlay", err);
  }

  return buildAnimatedGifFromImageElements(
    images,
    sizes,
    positions,
    sponsorImage,
    headerData,
    forSharing,
  );
};
